import type { BreakdownItem } from '@/components/organisms/FishGroupForms';
import type { OverviewCage } from '@/domain/fishGroups';
import type { AllocationSummary as AllocationSummaryData } from '@/routes/Internal/routes/Client/FishGroupsOverview/actions/operations/operateWizardHelpers';
import { parseAllocationCount } from '@/routes/Internal/routes/Client/FishGroupsOverview/actions/operations/operateWizardHelpers';
import type { WizardDestination } from '@/routes/Internal/routes/Client/FishGroupsOverview/actions/operations/operateWizardReducer';

export interface EmptyCandidate {
    cageId: number;
    cageName: string;
}

export interface OccupiedCandidate {
    cageId: number;
    cageName: string;
    occupantId: number;
    occupantName: string;
}

export interface SecondSourceCandidate {
    fishGroupId: number;
    name: string;
    cageId: number;
    cageName: string;
}

// Two pools of cage candidates. Empty cages let the operator pick a fresh location for the
// merged group; occupied-by-other cages provide the original "absorb a neighbour" flow. The
// initial source's own cage is excluded from both: it's reserved for the continuation row.

export function occupiedTargetCandidates(
    locationCages: OverviewCage[],
    initialSourceId: number
): OccupiedCandidate[] {
    return locationCages
        .filter(
            (cage) => cage.activeFishGroup !== null && cage.activeFishGroup.id !== initialSourceId
        )
        .map((cage) => ({
            cageId: cage.cageId,
            cageName: cage.cageName,
            occupantId: cage.activeFishGroup?.id ?? 0,
            occupantName: cage.activeFishGroup?.name ?? '',
        }));
}

export function emptyTargetCandidates(locationCages: OverviewCage[]): EmptyCandidate[] {
    return locationCages
        .filter((cage) => cage.activeFishGroup === null)
        .map((cage) => ({
            cageId: cage.cageId,
            cageName: cage.cageName,
        }));
}

/**
 * Candidates for the inline second-source picker on the empty branch. Same as the merge
 * step's source list: any active FG in the location except the initial one.
 */
export function secondSourceCandidates(
    locationCages: OverviewCage[],
    initialSourceId: number
): SecondSourceCandidate[] {
    return locationCages
        .filter(
            (cage) => cage.activeFishGroup !== null && cage.activeFishGroup.id !== initialSourceId
        )
        .map((cage) => ({
            fishGroupId: cage.activeFishGroup?.id ?? 0,
            name: cage.activeFishGroup?.name ?? '',
            cageId: cage.cageId,
            cageName: cage.cageName,
        }));
}

export interface TargetAllocationInput {
    continuationCountRaw: string;
    initialPopulation: number | null | undefined;
    secondSourceId: number | null;
    secondSourcePopulation: number | null | undefined;
    mergeTargetCountRaw: string;
}

export interface TargetAllocation {
    summary: AllocationSummaryData;
    /** What the initial source has left for the merge target after its continuation. */
    remainingFromInitial: number | null;
}

/**
 * Step-scoped allocation summary. Different from the global helper used by other steps: here
 * the pool is what's left to feed the merge target (initial minus continuation, plus the second
 * source's population), and the only "allocated" number we care about is the merge target's
 * own estimated count. Showing the full operation's sums in this step felt confusing: the
 * operator is focused on the merge target, so the numbers should be too.
 */
export function targetAllocation(input: TargetAllocationInput): TargetAllocation {
    const continuationCount = parseAllocationCount(input.continuationCountRaw) ?? 0;
    const remainingFromInitial =
        input.initialPopulation != null
            ? Math.max(0, input.initialPopulation - continuationCount)
            : null;
    // When no second source is picked yet, the pool is "remaining from initial" alone. Once a
    // second source is chosen (occupied branch sets it automatically, empty branch via the
    // inline picker) the pool grows to include that FG's full population.
    const fromSecond: number | null =
        input.secondSourceId == null ? 0 : (input.secondSourcePopulation ?? null);
    const totalSourcePopulation =
        remainingFromInitial != null && fromSecond != null
            ? remainingFromInitial + fromSecond
            : null;
    const anySourceUnknown = remainingFromInitial == null || fromSecond == null;
    const totalAllocated = parseAllocationCount(input.mergeTargetCountRaw) ?? 0;
    let remaining: number | null = null;
    let overage: number | null = null;
    if (totalSourcePopulation != null) {
        const diff = totalSourcePopulation - totalAllocated;
        if (diff >= 0) remaining = diff;
        else overage = -diff;
    }
    return {
        summary: { totalSourcePopulation, anySourceUnknown, totalAllocated, remaining, overage },
        remainingFromInitial,
    };
}

export interface TargetBreakdownInput {
    samTargetMode: 'occupied' | 'empty' | null;
    initialSourceId: number;
    /** Already translated. */
    splitPortionLabel: string;
    remainingFromInitial: number | null;
    secondSourceId: number | null;
    secondSourceName: string;
    secondSourcePopulation: number | null | undefined;
}

/**
 * The step-specific breakdown for the summary card. Item 1 is the split portion taken from
 * the initial source (a derived figure: the operator's continuation entry is what defines
 * it). Item 2 is the second source's full population, present once that source is chosen.
 * The card shows the breakdown only when both items exist (≥ 2 entries), which keeps the view
 * honest: a single-line breakdown duplicates the headline number.
 */
export function targetBreakdown(input: TargetBreakdownInput): BreakdownItem[] {
    const breakdown: BreakdownItem[] = [];
    if (input.samTargetMode != null) {
        breakdown.push({
            key: `initial-portion-${input.initialSourceId}`,
            label: input.splitPortionLabel,
            population: input.remainingFromInitial,
        });
    }
    if (input.secondSourceId != null) {
        breakdown.push({
            key: `second-source-${input.secondSourceId}`,
            label: input.secondSourceName,
            population: input.secondSourcePopulation ?? null,
        });
    }
    return breakdown;
}

/**
 * Resolve the absorbed fish group's display name from the merge target's
 * receivesFrom (the second inflow is the occupant). Used only in the
 * occupied branch — in the empty branch the absorbed FG comes from the
 * inline picker, which already shows the FG name in the list.
 */
export function absorbedNameForOccupied(
    target: WizardDestination,
    initialSourceId: number,
    candidates: OccupiedCandidate[]
): string {
    const occupantInflow = target.receivesFrom.find((i) => i.sourceFishGroupId !== initialSourceId);
    if (!occupantInflow) return '';
    const candidate = candidates.find((c) => c.cageId === target.cageId);
    return candidate?.occupantName ?? '';
}
